'use client';

import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';

export function WorkflowSteps() {
  const t = useTranslations('landing.workflow');

  const steps = [
    {
      number: '01',
      title: t('step1Title'),
      description: t('step1Desc'),
      mockup: (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full bg-white border border-black/8 rounded-lg overflow-hidden shadow-sm">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-black/5 bg-[#fafafa]">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-red-400/80" />
                <div className="w-2 h-2 rounded-full bg-yellow-400/80" />
                <div className="w-2 h-2 rounded-full bg-green-400/80" />
              </div>
              <div className="h-1.5 w-20 bg-black/8 rounded-full ml-2" />
            </div>
            <div className="p-3 flex flex-col gap-2.5">
              <div>
                <div className="h-1 w-10 bg-black/20 rounded-full mb-1.5" />
                <div className="h-7 w-full bg-[#f5f5f5] border border-black/8 rounded flex items-center px-2">
                  <span className="text-[8px] text-black/50 font-mono">
                    Senior Frontend Engineer
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="h-1 w-12 bg-black/15 rounded-full mb-1.5" />
                  <div className="h-6 w-full bg-[#f5f5f5] border border-black/8 rounded flex items-center px-2">
                    <span className="text-[7px] text-black/40 font-mono">Engineering</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="h-1 w-8 bg-black/15 rounded-full mb-1.5" />
                  <div className="h-6 w-full bg-[#f5f5f5] border border-black/8 rounded flex items-center px-2">
                    <span className="text-[7px] text-black/40 font-mono">L5 — Senior</span>
                  </div>
                </div>
              </div>
              <div>
                <div className="h-1 w-16 bg-black/15 rounded-full mb-1.5" />
                <div className="flex flex-wrap gap-1.5">
                  {['React', 'TypeScript', 'System Design', 'APIs'].map((s) => (
                    <div
                      key={s}
                      className="h-4 px-2 bg-primary/10 border border-primary/20 rounded-full flex items-center"
                    >
                      <span className="text-[7px] text-primary font-medium">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-5 px-2.5 bg-emerald-50 border border-emerald-200 rounded-full flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span className="text-[7px] text-emerald-600 font-medium">
                    Framework generated
                  </span>
                </div>
                <div className="h-1 w-10 bg-black/5 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      number: '02',
      title: t('step2Title'),
      description: t('step2Desc'),
      mockup: (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full bg-white border border-black/8 rounded-lg overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-3 py-2 border-b border-black/5 bg-[#fafafa]">
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-20 bg-black/15 rounded-full" />
                <div className="h-4 px-2 bg-blue-50 border border-blue-200 rounded-full flex items-center">
                  <span className="text-[7px] text-blue-600 font-medium">5 questions</span>
                </div>
              </div>
              <div className="h-5 px-2 bg-black/5 rounded flex items-center">
                <span className="text-[7px] text-black/40">+ Add</span>
              </div>
            </div>
            <div className="p-2.5 flex flex-col gap-2">
              {[
                { q: 'Describe a complex UI you built', type: 'Behavioral', weight: 85 },
                { q: 'Walk through your system design', type: 'Technical', weight: 70 },
                { q: 'How do you handle trade-offs?', type: 'Situational', weight: 60 },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 p-2 bg-[#fafafa] border border-black/5 rounded-md"
                >
                  <div className="flex flex-col gap-0.5 mt-1 opacity-30">
                    <div className="w-2 h-0.5 bg-black/40 rounded-full" />
                    <div className="w-2 h-0.5 bg-black/40 rounded-full" />
                    <div className="w-2 h-0.5 bg-black/40 rounded-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[8px] text-black/70 font-medium truncate">
                        {item.q}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-3.5 px-1.5 rounded text-[6px] font-medium flex items-center ${item.type === 'Technical' ? 'bg-purple-50 text-purple-600' : item.type === 'Behavioral' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}
                      >
                        {item.type}
                      </div>
                      <div className="flex-1 h-1 bg-black/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-400 rounded-full"
                          style={{ width: `${item.weight}%` }}
                        />
                      </div>
                      <span className="text-[6px] text-black/30 font-mono">{item.weight}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      number: '03',
      title: t('step3Title'),
      description: t('step3Desc'),
      mockup: (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full h-full bg-white border border-black/8 rounded-lg relative overflow-hidden flex flex-col shadow-sm">
            <div className="flex items-center justify-between px-3 py-2 border-b border-black/5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[7px] text-red-500 font-mono font-medium">LIVE</span>
              </div>
              <span className="text-[7px] text-black/30 font-mono">12:34</span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-3 bg-[#fafafa]">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-purple-400/15 border border-black/8 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-white border border-black/10 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                  </div>
                </div>
                <div
                  className="absolute inset-0 rounded-full border border-primary/20 animate-ping"
                  style={{ animationDuration: '2s' }}
                />
              </div>
              <div className="flex items-end gap-[2px] h-5">
                {[3, 5, 8, 4, 7, 6, 3, 5, 8, 6, 4, 7, 5, 3].map((h, i) => (
                  <div
                    key={i}
                    className="w-[3px] bg-primary/60 rounded-full transition-all"
                    style={{
                      height: `${h * 12}%`,
                      animation: `waveBar 1.2s ease-in-out ${i * 0.08}s infinite alternate`,
                    }}
                  />
                ))}
              </div>
              <span className="text-[7px] text-black/30">AI is listening...</span>
            </div>
            <div className="px-3 py-2 border-t border-black/5 bg-white">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 rounded-full bg-primary/20 shrink-0 mt-0.5" />
                  <div className="h-1 w-[80%] bg-black/10 rounded-full" />
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-400/20 shrink-0 mt-0.5" />
                  <div className="h-1 w-[65%] bg-black/8 rounded-full" />
                </div>
              </div>
            </div>
          </div>
          <style jsx>{`
            @keyframes waveBar {
              0% {
                transform: scaleY(0.4);
              }
              100% {
                transform: scaleY(1);
              }
            }
          `}</style>
        </div>
      ),
    },
    {
      number: '04',
      title: t('step4Title'),
      description: t('step4Desc'),
      mockup: (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full bg-white border border-black/8 rounded-lg overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-3 py-2 border-b border-black/5 bg-[#fafafa]">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400/50 to-purple-400/50 border border-black/10" />
                <div>
                  <div className="h-1.5 w-16 bg-black/20 rounded-full" />
                  <div className="h-1 w-12 bg-black/10 rounded-full mt-1" />
                </div>
              </div>
              <div className="h-5 px-2 bg-emerald-50 border border-emerald-200 rounded-full flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-[7px] text-emerald-600 font-medium">Advance</span>
              </div>
            </div>
            <div className="p-3 flex gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 relative">
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke="rgba(0,0,0,0.05)"
                    strokeWidth="2"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke="rgba(255,108,92,0.7)"
                    strokeWidth="2"
                    strokeDasharray="94.2"
                    strokeDashoffset="14.1"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="text-[11px] font-mono text-primary font-bold relative z-10">
                  85
                </span>
              </div>
              <div className="flex-1 flex flex-col gap-1.5 justify-center">
                {[
                  { label: 'Technical', score: 90 },
                  { label: 'Communication', score: 82 },
                  { label: 'Problem Solving', score: 78 },
                ].map((r) => (
                  <div key={r.label} className="flex items-center gap-2">
                    <span className="text-[6px] text-black/40 w-14 truncate">{r.label}</span>
                    <div className="flex-1 h-1.5 bg-black/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${r.score}%`,
                          background:
                            r.score >= 85
                              ? 'rgba(52,211,153,0.7)'
                              : r.score >= 75
                                ? 'rgba(255,108,92,0.6)'
                                : 'rgba(251,191,36,0.6)',
                        }}
                      />
                    </div>
                    <span className="text-[6px] text-black/30 font-mono w-4 text-right">
                      {r.score}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-3 pb-3">
              <div className="bg-[#fafafa] border border-black/5 rounded p-2">
                <div className="h-1 w-full bg-black/8 rounded-full mb-1.5" />
                <div className="h-1 w-5/6 bg-black/6 rounded-full mb-1.5" />
                <div className="h-1 w-3/4 bg-black/4 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section
      id="four-steps"
      data-theme="light"
      className="py-[120px] relative overflow-hidden bg-white rounded-t-[2rem] -mt-8 z-[40]"
    >
      <div className="container max-w-[1440px] mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
          {/* Left Column: Section Title */}
          <div className="lg:w-[30%] shrink-0">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-black leading-[1.1] mb-6 sticky top-32">
              {t('headline1')}
              <br />
              <span className="text-black/40">{t('headline2')}</span>
            </h2>
          </div>

          {/* Right Column: Steps Grid */}
          <div className="lg:w-[70%] grid md:grid-cols-2 gap-x-12 gap-y-16 relative">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col relative"
              >
                {/* Header Row: Badge & Line */}
                <div className="flex items-center w-full mb-8 relative">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-mono text-sm relative z-10 ${index === 0 ? 'bg-black text-white' : 'bg-black/5 text-black/40 border border-black/10'}`}
                  >
                    {step.number}
                  </div>
                  {/* Connector Line */}
                  <div className="flex-1 h-[1px] bg-black/10 ml-4" />
                </div>

                {/* Text Content */}
                <h3 className="text-xl font-medium text-black mb-3">{step.title}</h3>
                <p className="text-black/60 text-sm leading-relaxed mb-8">{step.description}</p>

                {/* UI Mockup */}
                <div className="w-full aspect-[4/3] rounded-2xl bg-[#f5f5f5] border border-black/5 flex items-center justify-center overflow-hidden relative group mt-auto">
                  <div className="absolute inset-0 bg-gradient-to-t from-white/50 to-transparent z-10 pointer-events-none" />
                  <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:16px_16px]" />
                  <div className="relative z-20 w-full h-full group-hover:scale-[1.02] transition-transform duration-500">
                    {step.mockup}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
